import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/components/AuthProvider";
import { Plus, LogOut, Pencil, Trash2, ExternalLink } from "lucide-react";

function fmt(d) { try { return new Date(d).toLocaleDateString("de-DE"); } catch { return ""; } }

export default function AdminDashboard() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const load = () => {
    setLoading(true);
    api.get("/admin/articles").then((r) => setArticles(r.data)).catch(() => toast.error("Konnte Artikel nicht laden")).finally(() => setLoading(false));
  };
  useEffect(() => { load(); document.title = "Redaktion – Grüne Geldanlage"; }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Artikel wirklich löschen?")) return;
    try {
      await api.delete(`/admin/articles/${id}`);
      toast.success("Artikel gelöscht");
      load();
    } catch { toast.error("Löschen fehlgeschlagen"); }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="admin-dashboard">
      <header className="bg-white border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="font-serif text-xl">Grüne Geldanlage</Link>
            <Badge variant="secondary" className="rounded-none">Redaktion</Badge>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-neutral-600">{user?.email}</span>
            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-btn"><LogOut size={14} className="mr-2" /> Abmelden</Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-serif text-4xl mb-2">Artikel-Übersicht</h1>
            <p className="text-neutral-600">{articles.length} Artikel gesamt</p>
          </div>
          <Button onClick={() => navigate("/admin/artikel/neu")} className="bg-brand-primary hover:bg-brand-hover text-white rounded-none" data-testid="new-article-btn">
            <Plus size={16} className="mr-2" /> Neuer Artikel
          </Button>
        </div>

        <div className="bg-white border border-neutral-200">
          <table className="w-full">
            <thead className="bg-neutral-50 border-b border-neutral-200">
              <tr>
                <th className="text-left px-6 py-3 text-xs uppercase tracking-wider text-neutral-500">Titel</th>
                <th className="text-left px-6 py-3 text-xs uppercase tracking-wider text-neutral-500">Status</th>
                <th className="text-left px-6 py-3 text-xs uppercase tracking-wider text-neutral-500">Kategorie</th>
                <th className="text-left px-6 py-3 text-xs uppercase tracking-wider text-neutral-500">Veröffentlicht</th>
                <th className="text-right px-6 py-3 text-xs uppercase tracking-wider text-neutral-500">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {loading && (<tr><td colSpan={5} className="px-6 py-10 text-center text-neutral-500">Wird geladen…</td></tr>)}
              {!loading && articles.length === 0 && (<tr><td colSpan={5} className="px-6 py-10 text-center text-neutral-500">Noch keine Artikel.</td></tr>)}
              {articles.map((a) => (
                <tr key={a.id} className="border-b border-neutral-100 hover:bg-neutral-50" data-testid={`admin-article-row-${a.slug}`}>
                  <td className="px-6 py-4 font-medium max-w-md">{a.title}</td>
                  <td className="px-6 py-4">
                    <Badge variant={a.status === "published" ? "default" : "secondary"} className="rounded-none">
                      {a.status === "published" ? "Veröffentlicht" : "Entwurf"}
                    </Badge>
                  </td>
                  <td className="px-6 py-4 text-neutral-600">{a.category}</td>
                  <td className="px-6 py-4 text-neutral-600 text-sm">{fmt(a.published_at)}</td>
                  <td className="px-6 py-4 text-right">
                    <div className="inline-flex gap-2">
                      {a.status === "published" && (
                        <Button variant="ghost" size="sm" asChild><Link to={`/artikel/${a.slug}`} target="_blank" rel="noopener"><ExternalLink size={14} /></Link></Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/artikel/${a.id}`)} data-testid={`edit-${a.slug}`}><Pencil size={14} /></Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(a.id)} data-testid={`delete-${a.slug}`}><Trash2 size={14} className="text-destructive" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
